import { sitemapService, DbSitemap } from './sitemapService';
import { designSystemService, DesignSystem } from './designSystemService';
export interface Blueprint {
  id: string;
  name: string;
  sitemap: DbSitemap | null;
  designSystem: DesignSystem | null;
  updated_at: string;
  created_at: string;
}

export const blueprintService = {
  /**
   * Fetch all blueprints for a workspace. A blueprint is a paired sitemap and design system sharing the same ID.
   */
  async getBlueprintsByWorkspace(workspaceId: string): Promise<Blueprint[]> {
    const [sitemaps, designSystems] = await Promise.all([
      sitemapService.getSitemapsByWorkspace(workspaceId),
      designSystemService.getByWorkspace(workspaceId),
    ]);

    const blueprintMap = new Map<string, Blueprint>();

    // Process Sitemaps
    for (const sitemap of sitemaps) {
      blueprintMap.set(sitemap.id, {
        id: sitemap.id,
        name: sitemap.name,
        sitemap,
        designSystem: null,
        updated_at: sitemap.updated_at,
        created_at: sitemap.created_at,
      });
    }

    // Process Design Systems
    for (const ds of designSystems) {
      if (blueprintMap.has(ds.id)) {
        const bp = blueprintMap.get(ds.id)!;
        bp.designSystem = ds;
        // Use the most recent updated_at
        if (new Date(ds.updated_at) > new Date(bp.updated_at)) {
          bp.updated_at = ds.updated_at;
        }
      } else {
        blueprintMap.set(ds.id, {
          id: ds.id,
          name: ds.name,
          sitemap: null,
          designSystem: ds,
          updated_at: ds.updated_at,
          created_at: ds.created_at,
        });
      }
    }

    return Array.from(blueprintMap.values()).sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  },

  /**
   * Create a new blueprint (both sitemap and design system with the same UUID)
   */
  async createBlueprint(
    workspaceId: string,
    userId: string,
    baseName: string = 'Untitled Blueprint'
  ): Promise<Blueprint> {
    const id = crypto.randomUUID();
    
    // Auto-increment name if default name already exists
    let name = baseName;
    try {
      const existing = await this.getBlueprintsByWorkspace(workspaceId);
      const names = new Set(existing.map(b => b.name));
      if (names.has(name)) {
        let counter = 2;
        while (names.has(`${baseName} ${counter}`)) {
          counter++;
        }
        name = `${baseName} ${counter}`;
      }
    } catch {
      // If fetching existing fails, keep baseName
    }

    // Create both in parallel with the same ID
    const [sitemap, designSystem] = await Promise.all([
      sitemapService.createSitemap(workspaceId, userId, name, id),
      designSystemService.create({
        id,
        workspace_id: workspaceId,
        name,
      }),
    ]);

    return {
      id,
      name,
      sitemap,
      designSystem,
      updated_at: sitemap.updated_at,
      created_at: sitemap.created_at,
    };
  },

  /**
   * Rename a blueprint (renames both underlying records)
   */
  async renameBlueprint(id: string, name: string): Promise<void> {
    const promises: Promise<any>[] = [];
    
    // Try to rename both, ignoring errors if one doesn't exist
    promises.push(sitemapService.renameSitemap(id, name).catch(() => {}));
    promises.push(designSystemService.update(id, { name }).catch(() => {}));
    
    await Promise.all(promises);
  },

  /**
   * Delete a blueprint (deletes both underlying records)
   */
  async deleteBlueprint(id: string): Promise<void> {
    const promises: Promise<any>[] = [];
    
    // Try to delete both, ignoring errors if one doesn't exist
    promises.push(sitemapService.deleteSitemap(id).catch(() => {}));
    promises.push(designSystemService.delete(id).catch(() => {}));
    
    await Promise.all(promises);
  },

  /**
   * Duplicate a blueprint
   */
  async duplicateBlueprint(_id: string): Promise<void> {
    // Duplication can be complex for full sitemaps. Left as a stub for now.
    throw new Error('Duplication of full blueprints not fully implemented yet');
  }
};
